import type { CSSProperties } from 'react';

/**
 * One shared column spec for the compact campaign list (#162).
 *
 * The header row and every body row derive BOTH their grid track sizing
 * (`ROW_GRID`) and their per-column text alignment from this single source, so
 * a header cell can never drift out of line with the body cell beneath it.
 *
 * FE#255: the numeric columns (Contacts, Sent) were `textAlign: 'right'` and so
 * hugged the right edge of their narrow tracks — the label visually detached
 * toward the next column ("header sits over the wrong column") and the values
 * were not centered under the label. Centering them here fixes both, and because
 * the header cell and the body cell now read the SAME `align`, they cannot
 * diverge again.
 */
export type ColumnAlign = NonNullable<CSSProperties['textAlign']>;

export interface RowColumn {
  /** CSS grid track sizing for this column. */
  track: string;
  /** Header label. */
  label: string;
  /** Text alignment shared by the header cell and the body cell. */
  align: ColumnAlign;
}

export const ROW_COLUMNS: RowColumn[] = [
  { track: 'minmax(0,1fr)', label: 'Campaign', align: 'left' },
  { track: '150px', label: 'Audience', align: 'left' },
  { track: '72px', label: 'Contacts', align: 'center' },
  { track: '60px', label: 'Sent', align: 'center' },
  // FE#340: the column is polymorphic — a completed row shows when it SENT, a
  // pending row when it is SCHEDULED — so the header names both (each cell also
  // carries its own per-row label) and the track is widened for the "<label>
  // DD/MM/YYYY, HH:MM:SS" content.
  { track: '176px', label: 'Sent / Scheduled', align: 'left' },
  { track: '92px', label: 'Status', align: 'left' },
  // FE#259: a trailing always-visible actions (3-dot) menu column, so a list row
  // is no longer a menu blind spot. Empty header label — the icon speaks for it.
  { track: '36px', label: '', align: 'center' },
];

/**
 * Grid template shared by the header and every row — derived from the same
 * `ROW_COLUMNS` list, so the column COUNT can never diverge between the two.
 */
export const ROW_GRID = ROW_COLUMNS.map((c) => c.track).join(' ');

/**
 * The shared alignment for a column, looked up by its header label — used by the
 * body cells whose content is bespoke (icon + name, a status pill, …) and so
 * cannot be produced by a generic header-style map. Defaults to 'left' for an
 * unknown label rather than throwing.
 */
export function alignFor(label: string): ColumnAlign {
  const col = ROW_COLUMNS.find((c) => c.label === label);
  return col ? col.align : 'left';
}
